import React, { useState, useEffect } from "react";
import {
  Calendar as BigCalendar,
  momentLocalizer,
  Views,
} from "react-big-calendar";
import moment from "moment";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./calendarCustom.css";
import { authFetch } from "../api/api";
import { useTranslation } from "react-i18next";


const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

moment.locale("ru");
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(BigCalendar);



const API_URL = `${BASE_URL}/api/events/`;

const COLOR_CHOICES = [
  "red", "blue", "green", "yellow", "orange",
  "purple", "pink", "brown", "gray", "black"
];

const DEFAULT_COLORS = [
  "red", "green", "yellow", "blue",
  "orange", "purple", "pink", "brown",
  "gray", "black"
];

const STANDARD_EVENTS = [
  "Производство",
  "Пересылка на склад экспедитора в Китае",
  "На складе экспедитора в Китае",
  "Упаковка на складе экспедитора в Китае",
  "Пересылка в Россию",
  "На складе экспедитора в Москве",
  "Перегрузка на складе экспедитора в Москве",
  "Пересылка местной логистической компанией до конечного получателя в Москве",
  "Пересылка местной логистической компанией до конечного получателя в другом городе",
  "На складе местной логистической компании в городе назначения",
  "Доставка на склад клиента",
  "Груз получен, ожидание оплаты",
  "Заказ завершен",
];

const eventStyleGetter = (event) => ({
  style: {
    backgroundColor: event.color,
    borderRadius: "8px",
    color: "white",
    fontWeight: "bold",
    padding: "4px",
    border: "none",
    cursor: "pointer",
  },
});

const CalendarPage = ({ selectedOrders }) => {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(Views.MONTH);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formOrderId, setFormOrderId] = useState(null);
  const [message, setMessage] = useState(null);

  const { t } = useTranslation("CalendarPage");

  const getInitialFormData = () => ({
    title: "",
    description: "",
    start_time: moment().format("YYYY-MM-DDTHH:mm"),
    end_time: moment().add(1, "hours").format("YYYY-MM-DDTHH:mm"),
    status: "not_done",
    color: DEFAULT_COLORS[0],
    comments: "",
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Загрузка событий с API
  useEffect(() => {
    if (!selectedOrders.length) return;

    const orderIds = selectedOrders.map((o) => o.id);

    authFetch(API_URL)
      .then((res) => res.json())
      .then((data) => {
        const filtered = data
          .filter((e) => orderIds.includes(e.order))
          .map((e) => ({
            id: e.id,
            title: e.title,
            start: new Date(e.start_time),
            end: new Date(e.end_time),
            color: e.color || DEFAULT_COLORS[0],
            comments: e.comments,
            orderId: e.order,
            customTitle: STANDARD_EVENTS.includes(e.title) ? "" : e.title,
          }));
        setEvents(filtered);
      })
      .catch((err) => console.error("Ошибка загрузки событий:", err));
  }, [selectedOrders]);

  const handleSelectSlot = () => {
    if (!selectedOrders.length) {
      setMessage(t("select_order_warning"));
      setTimeout(() => setMessage(null), 2500);
      return;
    }
    setFormOrderId(selectedOrders.length === 1 ? selectedOrders[0].id : null);
    setFormData(getInitialFormData());
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleSelectEvent = (event) => {
    setEditingEvent(event);
    setFormOrderId(event.orderId);
    setFormData({
      title: STANDARD_EVENTS.includes(event.title) ? event.title : "Другое",
      description: event.description || "",
      start_time: moment(event.start).format("YYYY-MM-DDTHH:mm"),
      end_time: moment(event.end).format("YYYY-MM-DDTHH:mm"),
      status: event.status || "not_done",
      color: event.color || DEFAULT_COLORS[0],
      comments: event.comments || "",
    });
    setShowForm(true);
  };

  // Сохранение события (создание или редактирование)
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!formOrderId) {
      setMessage("Выберите заказ");
      setTimeout(() => setMessage(null), 2500);
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: new Date(formData.end_time).toISOString(),
      status: formData.status,
      color: formData.color,
      comments: formData.comments,
      order: formOrderId,
    };

    try {
      const method = editingEvent ? "PUT" : "POST";
      const url = editingEvent ? `${API_URL}${editingEvent.id}/` : API_URL;

      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        console.error(t("save_error") + ":", errData);
        throw new Error(t("save_error"));
      }

      const newEvent = await res.json();
      const eventForCalendar = {
        id: newEvent.id,
        title: newEvent.title,
        description: newEvent.description,
        start: new Date(newEvent.start_time),
        end: new Date(newEvent.end_time),
        status: newEvent.status,
        color: newEvent.color,
        comments: newEvent.comments,
        orderId: newEvent.order,
      };

      setEvents((prev) =>
        editingEvent
          ? prev.map((e) => (e.id === editingEvent.id ? eventForCalendar : e))
          : [...prev, eventForCalendar]
      );

      setShowForm(false);
      setEditingEvent(null);
      setFormData(getInitialFormData());
    } catch (err) {
      console.error(err);
      setMessage(t("save_error"));
    }
  };

  // Удаление события
  const handleDeleteEvent = async () => {
    if (!editingEvent) return;

    try {
      const res = await authFetch(`${API_URL}${editingEvent.id}/`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(t("delete_error"));

      setEvents((prev) => prev.filter((e) => e.id !== editingEvent.id));
      setShowForm(false);
      setEditingEvent(null);
      setFormData(getInitialFormData());
    } catch (err) {
      console.error(err);
      setMessage(t("delete_error"));
    }
  };

  // Обновление события при перетаскивании или изменении размера
  const handleEventChange = async ({ event, start, end }) => {
    const updatedEvent = {
      ...event,
      start_time: new Date(start).toISOString(),
      end_time: new Date(end).toISOString(),
      title: event.customTitle || event.title,
      color: event.color || DEFAULT_COLORS[0],
    };

    try {
      await authFetch(`${API_URL}${event.id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedEvent),
      });

      setEvents((prev) =>
        prev.map((e) =>
          e.id === event.id
            ? { ...e, start: new Date(start), end: new Date(end), title: updatedEvent.title }
            : e
        )
      );
    } catch (err) {
      console.error("Ошибка при обновлении события:", err);
    }
  };

  return (
    <div className="calendar-container" style={{ marginTop: "64px" }}>
      <h2 className="text-xl font-semibold mb-3 px-4">{t("calendar")}</h2>

      <DnDCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        style={{ height: "calc(100vh - 120px)", padding: "0 16px" }}
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventChange}
        onEventResize={handleEventChange}
        resizable
        eventPropGetter={eventStyleGetter}
      />

      {message && (
        <div className="fixed bottom-6 right-6 bg-red-600 text-white px-4 py-3 rounded-xl shadow-lg">
          {message}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <form
            onSubmit={handleSaveEvent}
            className="bg-white rounded-2xl p-6 w-[420px] shadow-2xl space-y-4"
          >
            <h3 className="text-lg font-semibold text-center">
              {editingEvent 
                ? t("edit_event")  
                : t("new_event")
              }
            </h3>

            <div>
              <label className="block text-sm font-medium mb-1">{t("event_name")}</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("description")}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("start")}:</label>
              <input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("end")}:</label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("status")}:</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="not_done">{t("status_not_done")}</option>
                <option value="done">{t("status_done")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("color")}:</label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                {COLOR_CHOICES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("comment")}:</label>
              <textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              >
                {t("cancel")}
              </button>
              <div className="flex gap-2">
                {editingEvent && (
                  <button
                    type="button"
                    onClick={handleDeleteEvent}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    {t("delete")}
                  </button>
                )}
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;